/**
 * Technical SEO Utilities
 * Handles Core Web Vitals, structured data, and performance optimization
 */

import { Locale } from '../../i18n-config';
import { GLOBAL_AREA_SERVED } from './international-seo';
import { SUM_AGGREGATE_RATING } from './seo-rating';

// Organization Schema
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Super User Manager',
    alternateName: ['SUM', 'Super User Manager'],
    url: 'https://super-user-manager.com',
    logo: 'https://cdn.1cdn.app/application/UMD/2405110558455_logo.png',
    description: 'Customer management and loyalty program platform for small and medium businesses',
    email: 'info@super-user-manager.com',
    telephone: '+41 78 314 55 98',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CH',
      addressRegion: 'Switzerland',
    },
    foundingDate: '2021',
    numberOfEmployees: '2-10',
    industry: 'Software Development',
    knowsAbout: [
      'Customer Management',
      'Loyalty Programs',
      'User Authentication',
      'Business Analytics',
      'Customer Retention',
    ],
    sameAs: ['https://www.linkedin.com/company/super-user-manager'],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+41 78 314 55 98',
      contactType: 'Customer Support',
      availableLanguage: ['English', 'French'],
      hoursAvailable: 'Mo-Fr 09:00-17:00',
    },
  };
}

// Website Schema
export function generateWebsiteSchema(locale: Locale) {
  const baseUrl = 'https://super-user-manager.com';
  const localeUrl = locale === 'en-US' ? baseUrl : `${baseUrl}/${locale}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Super User Manager',
    url: localeUrl,
    description: locale.startsWith('fr')
      ? 'Plateforme de gestion des clients et programmes de fidélité pour PME'
      : 'Customer management and loyalty program platform for SMEs',
    inLanguage: locale,
    publisher: {
      '@type': 'Organization',
      name: 'Super User Manager',
    },
  };
}

// Locale-based currency mapping for structured data
export function getCurrencyForLocale(locale: string): string {
  if (locale.includes('US')) {
    return 'USD';
  }
  if (locale.includes('CH')) {
    return 'CHF';
  }
  return 'EUR';
}

// Format price with currency symbol based on locale
export function formatPriceWithCurrency(price: number, locale: string): string {
  const currency = getCurrencyForLocale(locale);

  const currencySymbols: { [key: string]: { symbol: string; position: 'before' | 'after' } } = {
    USD: { symbol: '$', position: 'before' },
    CHF: { symbol: 'CHF ', position: 'before' },
    EUR: { symbol: '€', position: 'after' },
  };

  const { symbol, position } = currencySymbols[currency] || { symbol: currency, position: 'before' };

  // Format the number with appropriate decimal places
  const formattedPrice = price % 1 === 0 ? price.toString() : price.toFixed(2);

  return position === 'before' ? `${symbol}${formattedPrice}` : `${formattedPrice}${symbol}`;
}

export function generateSoftwareSchema(locale: Locale) {
  const currency = getCurrencyForLocale(locale);

  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Super User Manager',
    image: 'https://cdn.1cdn.app/application/UMD/2405110558455_logo.png',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web Browser',
    applicationSubCategory: 'Customer Management Software',
    browserRequirements: 'Requires JavaScript. Requires HTML5.',
    softwareVersion: '2.2.4.0',
    releaseNotes: locale.startsWith('fr')
      ? 'Nouvelle interface utilisateur améliorée et fonctionnalités de fidélité avancées'
      : 'Enhanced user interface and advanced loyalty features',
    dateModified: '2025-12-15',
    creator: {
      '@type': 'Organization',
      name: 'Super User Manager',
    },
    // Locale-based offers - currency matches user's region
    offers: [
      {
        '@type': 'Offer',
        name: locale.startsWith('fr') ? 'Plan Gratuit' : 'Free Plan',
        price: '0',
        priceCurrency: currency,
        description: locale.startsWith('fr')
          ? 'Gratuit pour toujours - 25 clients inclus'
          : 'Free forever - 25 customers included',
      },
      {
        '@type': 'Offer',
        name: locale.startsWith('fr') ? 'Petite Entreprise' : 'Small Business',
        price: '29',
        priceCurrency: currency,
        description: locale.startsWith('fr')
          ? 'Tout ce dont vous avez besoin pour booster les ventes'
          : 'Everything you need to boost sales',
      },
      {
        '@type': 'Offer',
        name: locale.startsWith('fr') ? 'Grande Entreprise' : 'Large Business',
        price: '129',
        priceCurrency: currency,
        description: locale.startsWith('fr')
          ? 'Outils avancés et accès employés pour les entreprises en croissance'
          : 'Advanced tools and employee access for growing businesses',
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: SUM_AGGREGATE_RATING.ratingValue,
      reviewCount: SUM_AGGREGATE_RATING.reviewCount,
      bestRating: SUM_AGGREGATE_RATING.bestRating,
      worstRating: SUM_AGGREGATE_RATING.worstRating,
    },
    review: [
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Maria Rodriguez',
          jobTitle: 'Business Owner',
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: SUM_AGGREGATE_RATING.ratingValue,
          bestRating: SUM_AGGREGATE_RATING.bestRating,
          worstRating: SUM_AGGREGATE_RATING.worstRating,
        },
        reviewBody: locale.startsWith('fr')
          ? 'Super User Manager a transformé notre café. Configuration en 10 minutes, nos clients adorent le système QR code.'
          : 'Super User Manager transformed our café. Setup took 10 minutes and our customers love the QR code system.',
        datePublished: '2024-06-15',
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'James Kumar',
          jobTitle: 'Business Owner',
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: SUM_AGGREGATE_RATING.ratingValue,
          bestRating: SUM_AGGREGATE_RATING.bestRating,
          worstRating: SUM_AGGREGATE_RATING.worstRating,
        },
        reviewBody: locale.startsWith('fr')
          ? 'Augmentation de 35% de la rétention client. Tableau de bord simple et rapports excellents.'
          : '35% increase in customer retention. Simple dashboard and excellent reports.',
        datePublished: '2024-07-02',
      },
    ],
    description: locale.startsWith('fr')
      ? 'Solution complète de gestion clientèle avec programmes de fidélité intégrés pour PME. Configuration en 3 minutes, augmentation moyenne de 25% de la rétention client.'
      : 'Complete customer management solution with integrated loyalty programs for SMEs. 3-minute setup, 25% average customer retention increase.',
    // Features aligned with pricing page + strategic features
    features: locale.startsWith('fr')
      ? [
          'Tableau de bord client',
          'Programme de fidélité numérique',
          'Système de gamification',
          'Inscription par QR code',
          "Formulaire d'inscription personnalisable",
          'Formulaires de capture de leads',
          'Campagnes SMS marketing',
          'Campagnes email marketing',
          'Fichiers et documents sécurisés',
          'Cartes de visite digitales',
          'Pages publiques personnalisables',
          'Analyses clients avancées',
          'Offres personnalisées',
          'Gestion des récompenses',
          'Système de points et niveaux',
          'Configuration 10 minutes',
          'API REST complète',
          'Support prioritaire',
          'Augmentation fidélisation 20%',
        ]
      : [
          'Customer dashboard',
          'Digital Loyalty Program',
          'Gamification system',
          'QR code registration',
          'Customizable sign-up form',
          'Lead capture forms',
          'SMS marketing campaigns',
          'Email marketing campaigns',
          'Secure files & documents',
          'Digital business cards',
          'Customizable public pages',
          'Advanced customer insights',
          'Personalized offers',
          'Rewards management',
          'Points & tiers system',
          '10-minute setup',
          'Complete REST API',
          'Priority support',
          '20% retention boost',
        ],
    screenshot: 'https://cdn.1cdn.app/application/UMD/25122612511023_sum_dashboard.png',
    softwareHelp: {
      '@type': 'CreativeWork',
      url: locale.startsWith('fr')
        ? 'https://super-user-manager.com/fr-FR/faq'
        : 'https://super-user-manager.com/en-US/faq',
    },
    softwareRequirements: locale.startsWith('fr')
      ? 'Navigateur web moderne (Chrome, Firefox, Safari, Edge)'
      : 'Modern web browser (Chrome, Firefox, Safari, Edge)',
    memoryRequirements: '256MB',
    storageRequirements: '30MB',
    processorRequirements: 'Any modern processor',
    permissions: 'No special permissions required',
    countriesSupported: ['CH', 'FR', 'DE', 'IT', 'AT', 'US', 'CA', 'GB'],
    inLanguage: [locale],
  };
}

// FAQ Schema - Uses EUR for French, USD for English
export function generateFAQSchema(locale: Locale) {
  const faqs = locale.startsWith('fr')
    ? [
        {
          question: "Qu'est-ce que Super User Manager?",
          answer:
            'Super User Manager est une plateforme complète de gestion clientèle avec programmes de fidélité intégrés, spécialement conçue pour les petites et moyennes entreprises. Elle combine un tableau de bord client simple avec des outils de croissance puissants.',
        },
        {
          question: 'Combien coûte Super User Manager?',
          answer: `Super User Manager est gratuit pour 25 clients. Nos plans payants commencent à ${formatPriceWithCurrency(
            29,
            locale
          )}/mois en annuel pour 1 000 clients, et ${formatPriceWithCurrency(
            129,
            locale
          )}/mois en annuel pour 5 000 clients. Au-delà, le tarif est sur devis.`,
        },
        {
          question: 'Comment fonctionne le programme de fidélité digital?',
          answer:
            "Les clients scannent un QR code en magasin, s'inscrivent en quelques secondes, accumulent des points automatiquement et reçoivent des récompenses. Aucune application à télécharger, aucune configuration technique requise.",
        },
        {
          question: 'Combien de temps faut-il pour configurer Super User Manager?',
          answer:
            "La configuration complète prend seulement 10 minutes. Vous créez votre programme de fidélité, personnalisez vos couleurs et messages, puis affichez le QR code dans votre magasin. C'est tout!",
        },
        {
          question: "Quels types d'entreprises utilisent Super User Manager?",
          answer:
            'Super User Manager est parfait pour les cafés, restaurants, magasins de détail, salons de beauté, centres de fitness, et toute PME qui souhaite fidéliser ses clients et augmenter ses ventes.',
        },
        {
          question: 'Puis-je vraiment augmenter ma rétention client de 25%?',
          answer:
            "Oui, nos clients voient en moyenne une augmentation de 25% de la rétention client dans les 3 premiers mois. Certains atteignent même 40-60% d'amélioration avec une utilisation optimale.",
        },
        {
          question: 'Y a-t-il une garantie ou un essai gratuit?',
          answer:
            'Oui! Vous pouvez commencer gratuitement avec 25 clients pour toujours. De plus, nous offrons une garantie de remboursement optionnelle de 30 jours sur tous nos plans payants.',
        },
      ]
    : [
        {
          question: 'What is Super User Manager?',
          answer:
            'Super User Manager is a complete customer management platform with integrated loyalty programs, specifically designed for small and medium businesses. It combines a simple customer dashboard with powerful growth tools.',
        },
        {
          question: 'How much does Super User Manager cost?',
          answer: `Super User Manager is free for 25 customers. Our paid plans start at ${formatPriceWithCurrency(
            29,
            locale
          )}/month billed annually for 1,000 customers, and ${formatPriceWithCurrency(
            129,
            locale
          )}/month billed annually for 5,000 customers. Above that, pricing is on quote.`,
        },
        {
          question: 'How does the digital loyalty program work?',
          answer:
            'Customers scan a QR code in-store, register in seconds, automatically accumulate points, and receive rewards. No app download required, no technical setup needed.',
        },
        {
          question: 'How long does it take to set up Super User Manager?',
          answer:
            "Complete setup takes just 10 minutes. You create your loyalty program, customize your colors and messages, then display the QR code in your store. That's it!",
        },
        {
          question: 'What types of businesses use Super User Manager?',
          answer:
            'Super User Manager is perfect for cafés, restaurants, retail stores, beauty salons, fitness centers, and any SME that wants to retain customers and increase sales.',
        },
        {
          question: 'Can I really increase customer retention by 25%?',
          answer:
            'Yes, our customers see an average 25% increase in customer retention within the first 3 months. Some achieve even 40-60% improvement with optimal usage.',
        },
        {
          question: 'Is there a guarantee or free trial?',
          answer:
            'Yes! You can start free with 25 customers forever. Plus, we offer an optional 30-day money-back guarantee on all our paid plans.',
        },
      ];

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// Breadcrumb Schema
export function generateBreadcrumbSchema(locale: Locale, path: string) {
  const baseUrl = 'https://super-user-manager.com';
  // Keep the locale segment even for the default locale: canonical() does, and a locale-less
  // URL is redirected by the middleware.
  const localeUrl = `${baseUrl}/${locale}`;

  const pathSegments = path.split('/').filter(Boolean);
  const breadcrumbs = [
    {
      '@type': 'ListItem',
      position: 1,
      name: locale.startsWith('fr') ? 'Accueil' : 'Home',
      item: localeUrl,
    },
  ];

  let currentPath = localeUrl;
  pathSegments.forEach((segment) => {
    if (segment !== locale) {
      currentPath += `/${segment}`;
      breadcrumbs.push({
        '@type': 'ListItem',
        position: breadcrumbs.length + 1,
        name: formatBreadcrumbName(segment, locale),
        item: currentPath,
      });
    }
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs,
  };
}

function formatBreadcrumbName(segment: string, locale: Locale): string {
  const names: { [key: string]: { en: string; fr: string } } = {
    features: { en: 'Features', fr: 'Fonctionnalités' },
    pricing: { en: 'Pricing', fr: 'Tarifs' },
    'contact-us': { en: 'Contact', fr: 'Contact' },
    faq: { en: 'FAQ', fr: 'FAQ' },
    'success-stories': { en: 'Success Stories', fr: 'Témoignages' },
    'why-choose-sum': { en: 'Why Choose SUM', fr: 'Pourquoi Choisir SUM' },
    'loyalty-programs': { en: 'Loyalty Programs', fr: 'Programmes Fidélité' },
    'our-teams': { en: 'Our Team', fr: 'Notre Équipe' },
    'privacy-policy': { en: 'Privacy Policy', fr: 'Politique de Confidentialité' },
    'business-cards': { en: 'Digital Business Cards', fr: 'Cartes de Visite digitales' },
    gaming: { en: 'Gamification system', fr: 'Système de gamification' },
    'ai-engine': { en: 'AI Engine', fr: 'Moteur IA' },
    'auto-pilot': { en: 'Auto-Pilot', fr: 'Auto-Pilot' },
    b2b: { en: 'B2B', fr: 'B2B' },
    callbot: { en: 'Callbot', fr: 'Agent vocal' },
    crm: { en: 'CRM', fr: 'CRM' },
    demo: { en: 'Request a Demo', fr: 'Demander une démo' },
    'discover-business-cards': {
      en: 'Discover Digital Business Cards',
      fr: 'Découvrir les Cartes de Visite digitales',
    },
    distributor: { en: 'Distributor Program', fr: 'Programme Distributeur' },
    documentations: { en: 'Documentation', fr: 'Documentation' },
    'e-signature': { en: 'E-Signature', fr: 'Signature électronique' },
    'event-management': { en: 'Event Management', fr: "Gestion d'événements" },
    events: { en: 'Events', fr: 'Événements' },
    'google-reviews': { en: 'Google Reviews', fr: 'Avis Google' },
    'login-page': { en: 'Login Page', fr: 'Page de connexion' },
    'loyalty-app': { en: 'Loyalty App', fr: 'App de fidélité' },
    'mcp-claude': { en: 'MCP for Claude Code', fr: 'MCP pour Claude Code' },
    'online-booking': { en: 'Online Booking', fr: 'Agenda et réservation en ligne' },
    payments: { en: 'Payments', fr: 'Paiements' },
    retail: { en: 'Retail', fr: 'Commerce de détail' },
    restaurants: { en: 'Restaurants', fr: 'Restauration' },
    'roi-calculator': { en: 'ROI Calculator', fr: 'Calculateur ROI' },
    services: { en: 'Services', fr: 'Services' },
    'signup-forms': { en: 'Signup Forms', fr: "Formulaires d'inscription" },
    tos: { en: 'Terms of Service', fr: "Conditions d'utilisation" },
    wallet: { en: 'Mobile Wallet', fr: 'Wallet mobile' },
  };

  const segmentName = names[segment];
  if (segmentName) {
    return locale.startsWith('fr') ? segmentName.fr : segmentName.en;
  }
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
}

// Core Web Vitals monitoring script
export function generateCWVScript() {
  return `
    (function() {
      function measureCWV() {
        try {
          // Largest Contentful Paint
          new PerformanceObserver((entryList) => {
            const entries = entryList.getEntries();
            const lastEntry = entries[entries.length - 1];
            if (window.gtag) {
              gtag('event', 'LCP', {
                value: Math.round(lastEntry.startTime),
                custom_parameter_1: lastEntry.startTime > 2500 ? 'needs_improvement' : 'good'
              });
            }
          }).observe({entryTypes: ['largest-contentful-paint']});

          // First Input Delay
          new PerformanceObserver((entryList) => {
            for (const entry of entryList.getEntries()) {
              if (window.gtag) {
                gtag('event', 'FID', {
                  value: Math.round(entry.processingStart - entry.startTime),
                  custom_parameter_1: (entry.processingStart - entry.startTime) > 100 ? 'needs_improvement' : 'good'
                });
              }
            }
          }).observe({entryTypes: ['first-input']});

          // Cumulative Layout Shift
          let clsValue = 0;
          let clsEntries = [];
          new PerformanceObserver((entryList) => {
            for (const entry of entryList.getEntries()) {
              if (!entry.hadRecentInput) {
                clsEntries.push(entry);
                clsValue += entry.value;
              }
            }
            if (window.gtag) {
              gtag('event', 'CLS', {
                value: Math.round(clsValue * 1000) / 1000,
                custom_parameter_1: clsValue > 0.1 ? 'needs_improvement' : 'good'
              });
            }
          }).observe({entryTypes: ['layout-shift']});

        } catch (e) {
          console.warn('CWV measurement failed:', e);
        }
      }

      if (document.readyState === 'complete') {
        setTimeout(measureCWV, 0);
      } else {
        window.addEventListener('load', () => setTimeout(measureCWV, 0));
      }
    })();
  `;
}

// Generate all structured data for a page
export function generatePageStructuredData(
  locale: Locale,
  pageType: string,
  path: string,
  pageTitle?: string,
  pageDescription?: string
) {
  const structuredData: any[] = [
    generateOrganizationSchema(),
    generateWebsiteSchema(locale),
    generateLocalBusinessSchema(locale),
  ];

  // Add page-specific schemas
  switch (pageType) {
    case 'home':
      structuredData.push(generateSoftwareSchema(locale), generateHowToSchema(locale), generateReviewSchema(locale));
      break;

    case 'features':
      structuredData.push(
        generateSoftwareSchema(locale),
        generateServiceSchema(locale, 'customer-management'),
        generateServiceSchema(locale, 'loyalty-program'),
        generateServiceSchema(locale, 'analytics'),
        generateServiceSchema(locale, 'gamification'),
        generateServiceSchema(locale, 'digital-business-cards')
      );
      if (pageTitle && pageDescription) {
        structuredData.push(generateArticleSchema(locale, pageTitle, pageDescription, path));
      }
      break;

    case 'pricing':
      structuredData.push(generateSoftwareSchema(locale), generateServiceSchema(locale, 'customer-management'));
      if (pageTitle && pageDescription) {
        structuredData.push(generateArticleSchema(locale, pageTitle, pageDescription, path));
      }
      break;

    case 'faq':
      structuredData.push(generateFAQSchema(locale), generateSoftwareSchema(locale));
      if (pageTitle && pageDescription) {
        structuredData.push(generateArticleSchema(locale, pageTitle, pageDescription, path));
      }
      break;

    case 'success-stories':
    case 'testimonials':
      structuredData.push(generateReviewSchema(locale), generateSoftwareSchema(locale));
      if (pageTitle && pageDescription) {
        structuredData.push(generateArticleSchema(locale, pageTitle, pageDescription, path));
      }
      break;

    case 'why-choose-sum':
      structuredData.push(generateSoftwareSchema(locale), generateReviewSchema(locale));
      if (pageTitle && pageDescription) {
        structuredData.push(generateArticleSchema(locale, pageTitle, pageDescription, path));
      }
      break;

    case 'loyalty-programs':
      structuredData.push(
        generateServiceSchema(locale, 'loyalty-program'),
        generateSoftwareSchema(locale),
        generateHowToSchema(locale)
      );
      if (pageTitle && pageDescription) {
        structuredData.push(generateArticleSchema(locale, pageTitle, pageDescription, path));
      }
      break;

    case 'our-teams':
      if (pageTitle && pageDescription) {
        structuredData.push(generateArticleSchema(locale, pageTitle, pageDescription, path));
      }
      break;

    case 'contact-us':
      // Contact pages already have organization info, no additional schemas needed
      if (pageTitle && pageDescription) {
        structuredData.push(generateArticleSchema(locale, pageTitle, pageDescription, path));
      }
      break;

    default:
      // Default case for other pages
      structuredData.push(generateSoftwareSchema(locale));
      if (pageTitle && pageDescription) {
        structuredData.push(generateArticleSchema(locale, pageTitle, pageDescription, path));
      }
      break;
  }

  // Add breadcrumb for non-home pages
  if (path !== '/' && path !== '') {
    structuredData.push(generateBreadcrumbSchema(locale, path));
  }

  return structuredData;
}

// Enhanced function to generate comprehensive structured data based on page content
export function generateEnhancedPageStructuredData(
  locale: Locale,
  pageType: string,
  path: string,
  options?: {
    title?: string;
    description?: string;
    includeReviews?: boolean;
    includeHowTo?: boolean;
    includeServices?: boolean;
    customServices?: Array<
      'customer-management' | 'loyalty-program' | 'analytics' | 'gamification' | 'digital-business-cards'
    >;
  }
) {
  const structuredData = generatePageStructuredData(locale, pageType, path, options?.title, options?.description);

  // Add optional schemas based on page content
  if (options?.includeReviews) {
    structuredData.push(generateReviewSchema(locale));
  }

  if (options?.includeHowTo) {
    structuredData.push(generateHowToSchema(locale));
  }

  if (options?.includeServices && options?.customServices) {
    options.customServices.forEach((serviceType) => {
      structuredData.push(generateServiceSchema(locale, serviceType));
    });
  }

  return structuredData;
}

// Review Schema for testimonials
export function generateReviewSchema(locale: Locale) {
  const reviews = locale.startsWith('fr')
    ? [
        {
          author: 'Maria Rodriguez',
          business: 'Café Luna',
          rating: 5,
          reviewBody:
            'Super User Manager a transformé notre café. Nous avons vu 40% de clients fidèles en plus en seulement 3 mois. La configuration a pris 10 minutes et nos clients adorent le système QR code.',
          datePublished: '2024-06-15',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'James Kumar',
          business: "Mitchell's Hardware",
          rating: 5,
          reviewBody:
            'Augmentation de 35% de la rétention client depuis que nous utilisons Super User Manager. Le tableau de bord est simple et les rapports nous aident à comprendre nos clients.',
          datePublished: '2024-07-02',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'Jennifer Gagnon',
          business: "Restaurant L'hippodrome",
          rating: 5,
          reviewBody:
            "En 2 mois, nous avons gagné 60 nouveaux clients fidèles grâce au programme de fidélité digital. Les clients s'inscrivent en quelques secondes avec le QR code.",
          datePublished: '2024-07-20',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'Sophie Martin',
          business: 'Salon Beauté Plus',
          rating: 5,
          reviewBody:
            'Parfait pour notre salon. Les clientes accumulent des points automatiquement et reviennent plus souvent. Le support client est excellent.',
          datePublished: '2024-08-01',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'David Chen',
          business: 'FitZone Gym',
          rating: 4,
          reviewBody:
            "Très bon système pour fidéliser nos membres. Interface claire et setup rapide. Quelques fonctionnalités mineures à améliorer mais dans l'ensemble excellent.",
          datePublished: '2024-08-10',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'Emma Dubois',
          business: 'Boulangerie du Coin',
          rating: 5,
          reviewBody:
            'Nos clients réguliers adorent le programme de points. Nous avons doublé le nombre de visites répétées en 4 mois. Très facile à utiliser.',
          datePublished: '2024-08-15',
          itemReviewed: 'Super User Manager',
        },
      ]
    : [
        {
          author: 'Maria Rodriguez',
          business: 'Café Luna',
          rating: 5,
          reviewBody:
            'Super User Manager transformed our café. We saw 40% more repeat customers in just 3 months. Setup took 10 minutes and our customers love the QR code system.',
          datePublished: '2024-06-15',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'James Kumar',
          business: "Mitchell's Hardware",
          rating: 5,
          reviewBody:
            '35% increase in customer retention since using Super User Manager. The dashboard is simple and the reports help us understand our customers better.',
          datePublished: '2024-07-02',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'Jennifer Gagnon',
          business: "Restaurant L'hippodrome",
          rating: 5,
          reviewBody:
            'In 2 months, we gained 60 new loyal customers through the digital loyalty program. Customers sign up in seconds with the QR code.',
          datePublished: '2024-07-20',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'Sophie Martin',
          business: 'Beauty Plus Salon',
          rating: 5,
          reviewBody:
            'Perfect for our salon. Clients automatically accumulate points and return more often. Customer support is excellent.',
          datePublished: '2024-08-01',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'David Chen',
          business: 'FitZone Gym',
          rating: 4,
          reviewBody:
            'Very good system for member retention. Clear interface and quick setup. A few minor features to improve but overall excellent.',
          datePublished: '2024-08-10',
          itemReviewed: 'Super User Manager',
        },
        {
          author: 'Emma Wilson',
          business: 'Corner Bakery',
          rating: 5,
          reviewBody:
            'Our regular customers love the points program. We doubled repeat visits in 4 months. Very easy to use.',
          datePublished: '2024-08-15',
          itemReviewed: 'Super User Manager',
        },
      ];

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Super User Manager',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: locale.startsWith('fr')
      ? 'Solution complète de gestion clientèle avec programmes de fidélité intégrés pour PME.'
      : 'Complete customer management solution with integrated loyalty programs for SMEs.',
    image: [
      'https://cdn.1cdn.app/application/UMD/2405110558455_logo.png',
      'https://cdn.1cdn.app/application/UMD/25122612511023_sum_dashboard.png',
    ],
    author: {
      '@type': 'Organization',
      name: 'Super User Manager',
      url: 'https://super-user-manager.com',
    },
    offers: [
      {
        '@type': 'Offer',
        name: locale.startsWith('fr') ? 'Plan Gratuit' : 'Free Plan',
        price: '0',
        priceCurrency: getCurrencyForLocale(locale),
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: locale.startsWith('fr') ? 'Petite Entreprise' : 'Small Business',
        price: '29',
        priceCurrency: getCurrencyForLocale(locale),
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: locale.startsWith('fr') ? 'Grande Entreprise' : 'Large Business',
        price: '129',
        priceCurrency: getCurrencyForLocale(locale),
        availability: 'https://schema.org/InStock',
      },
    ],
    review: reviews.map((review) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.author,
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody: review.reviewBody,
      datePublished: review.datePublished,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: SUM_AGGREGATE_RATING.ratingValue,
      reviewCount: SUM_AGGREGATE_RATING.reviewCount,
      bestRating: SUM_AGGREGATE_RATING.bestRating,
      worstRating: SUM_AGGREGATE_RATING.worstRating,
    },
  };
}

// Local Business Schema
export function generateLocalBusinessSchema(locale: Locale) {
  return {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'LocalBusiness', 'SoftwareCompany'],
    name: 'Super User Manager',
    alternateName: ['SUM', 'Super User Manager AG'],
    legalName: 'Super User Manager AG',
    url: 'https://super-user-manager.com',
    logo: {
      '@type': 'ImageObject',
      url: 'https://cdn.1cdn.app/application/UMD/2405110558455_logo.png',
      width: 300,
      height: 300,
      caption: 'Super User Manager Logo',
    },
    image: [
      {
        '@type': 'ImageObject',
        url: 'https://cdn.1cdn.app/application/UMD/25122612511023_sum_dashboard.png',
        width: 1200,
        height: 630,
        caption: 'Super User Manager Dashboard',
      },
      {
        '@type': 'ImageObject',
        url: 'https://cdn.1cdn.app/application/UMD/2512261258082_gaming_image_sum.png',
        width: 800,
        height: 600,
        caption: 'Loyalty Program Feature',
      },
    ],
    description: locale.startsWith('fr')
      ? 'Société suisse spécialisée dans les solutions de gestion clientèle et programmes de fidélité pour PME. Basée en Suisse, nous aidons les entreprises à fidéliser leurs clients et augmenter leurs ventes.'
      : 'Swiss company specializing in customer management solutions and loyalty programs for SMEs. Based in Switzerland, we help businesses retain customers and increase sales.',
    slogan: locale.startsWith('fr') ? 'Gérez vos clients. Boostez la fidélité.' : 'Manage customers. Boost retention.',
    foundingDate: '2021',
    founders: [
      {
        '@type': 'Person',
        name: 'Super User Manager Team',
      },
    ],
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: 8,
      unitText: 'employees',
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CH',
      addressRegion: 'Switzerland',
      addressLocality: 'Switzerland',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 46.8182,
      longitude: 8.2275,
    },
    areaServed: GLOBAL_AREA_SERVED,
    serviceArea: {
      '@type': 'GeoCircle',
      geoMidpoint: {
        '@type': 'GeoCoordinates',
        latitude: 46.8182,
        longitude: 8.2275,
      },
      geoRadius: 'Worldwide',
    },
    email: 'info@super-user-manager.com',
    telephone: '+41 78 314 55 98',
    faxNumber: null,
    priceRange: locale.startsWith('fr')
      ? `Gratuit - ${formatPriceWithCurrency(129, locale)}/mois`
      : `Free - ${formatPriceWithCurrency(129, locale)}/month`,
    currenciesAccepted: ['CHF', 'EUR', 'USD'],
    paymentAccepted: ['Credit Card'],
    openingHours: 'Mo-Fr 09:00-17:00',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+41 78 314 55 98',
        contactType: 'Customer Support',
        availableLanguage: ['English', 'French', 'German'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '17:00',
        },
        areaServed: GLOBAL_AREA_SERVED,
      },
      {
        '@type': 'ContactPoint',
        contactType: 'Sales',
        availableLanguage: ['English', 'French'],
        hoursAvailable: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '09:00',
          closes: '17:00',
        },
      },
    ],
    sameAs: ['https://www.linkedin.com/company/super-user-manager'],
    knowsAbout: [
      'Customer Management',
      'Loyalty Programs',
      'User Authentication',
      'Business Analytics',
      'Customer Retention',
      'QR Code Technology',
      'Digital Marketing',
      'Small Business Solutions',
      'SaaS Development',
      'Customer Experience',
      'Digital Business Cards',
      'Gamification system',
    ],
    awards: [
      locale.startsWith('fr')
        ? `Solution SaaS recommandée pour PME ${new Date().getFullYear()}`
        : `Recommended SaaS Solution for SMEs ${new Date().getFullYear()}`,
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: locale.startsWith('fr') ? "Plans d'abonnement" : 'Subscription Plans',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: locale.startsWith('fr') ? 'Plan Gratuit' : 'Free Plan',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: locale.startsWith('fr') ? 'Petite Entreprise' : 'Small Business',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: locale.startsWith('fr') ? 'Entreprise en Croissance' : 'Growing Business',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: locale.startsWith('fr') ? 'Grande Entreprise' : 'Large Enterprise',
          },
        },
      ],
    },
    makesOffer: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: locale.startsWith('fr') ? 'Gestion Clientèle' : 'Customer Management',
          description: locale.startsWith('fr')
            ? 'Tableau de bord complet pour gérer vos clients'
            : 'Complete dashboard to manage your customers',
        },
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: locale.startsWith('fr') ? 'Programme de Fidélité' : 'Loyalty Program',
          description: locale.startsWith('fr')
            ? 'Programme de fidélité digital avec QR code'
            : 'Digital loyalty program with QR code',
        },
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: locale.startsWith('fr') ? 'Cartes de Visite Digitales' : 'Digital Business Cards',
          description: locale.startsWith('fr')
            ? 'Créez et partagez des cartes de visite digitales professionnelles avec QR code'
            : 'Create and share professional digital business cards with QR code',
        },
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: locale.startsWith('fr') ? "Formulaires d'Inscription" : 'Registration Forms',
          description: locale.startsWith('fr')
            ? "Formulaires d'inscription personnalisables pour accueillir des prospects pré-qualifiés directement dans votre base clients"
            : 'Customizable registration forms to onboard pre-qualified leads directly into your customer database',
        },
        availability: 'https://schema.org/InStock',
      },
    ],
    parentOrganization: null,
    subOrganization: [],
    department: [],
    brand: {
      '@type': 'Brand',
      name: 'Super User Manager',
      logo: 'https://cdn.1cdn.app/application/UMD/2405110558455_logo.png',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: SUM_AGGREGATE_RATING.ratingValue,
      reviewCount: SUM_AGGREGATE_RATING.reviewCount,
      bestRating: SUM_AGGREGATE_RATING.bestRating,
      worstRating: SUM_AGGREGATE_RATING.worstRating,
    },
  };
}

// Article Schema for content pages
export function generateArticleSchema(
  locale: Locale,
  title: string,
  description: string,
  path: string,
  datePublished?: string,
  dateModified?: string
) {
  const baseUrl = 'https://super-user-manager.com';
  const localeUrl = locale === 'en-US' ? baseUrl : `${baseUrl}/${locale}`;
  const fullUrl = `${localeUrl}${path}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    url: fullUrl,
    mainEntityOfPage: fullUrl,
    author: {
      '@type': 'Organization',
      name: 'Super User Manager',
      url: 'https://super-user-manager.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Super User Manager',
      logo: {
        '@type': 'ImageObject',
        url: 'https://cdn.1cdn.app/application/UMD/2405110558455_logo.png',
        width: 300,
        height: 300,
      },
    },
    datePublished: datePublished || '2025-01-01',
    dateModified: dateModified || new Date().toISOString().split('T')[0],
    inLanguage: locale,
    about: {
      '@type': 'Thing',
      name: locale.startsWith('fr') ? 'Gestion Clientèle' : 'Customer Management',
    },
    keywords: locale.startsWith('fr')
      ? ['gestion clientèle', 'programme fidélité', 'PME', 'SaaS', 'rétention client']
      : ['customer management', 'loyalty program', 'SME', 'SaaS', 'customer retention'],
    articleSection: locale.startsWith('fr') ? 'Solutions SaaS' : 'SaaS Solutions',
    wordCount: 500,
    articleBody: description,
  };
}

// How-to Schema for setup guides
export function generateHowToSchema(locale: Locale) {
  const steps = locale.startsWith('fr')
    ? [
        {
          name: 'Créer votre compte',
          text: 'Inscrivez-vous gratuitement sur Super User Manager en quelques secondes.',
          image: 'https://cdn.1cdn.app/application/UMD/25122613140997_registration.png',
        },
        {
          name: 'Configurer votre programme de fidélité',
          text: 'Personnalisez vos couleurs, messages et récompenses en 5 minutes.',
          image: 'https://cdn.1cdn.app/application/UMD/25122613141056_setup.png',
        },
        {
          name: 'Afficher le QR code en magasin',
          text: "Imprimez et affichez votre QR code pour que les clients puissent s'inscrire.",
          image: 'https://cdn.1cdn.app/application/UMD/2512261314097_qr.png',
        },
      ]
    : [
        {
          name: 'Create your account',
          text: 'Sign up for Super User Manager for free in seconds.',
          image: 'https://cdn.1cdn.app/application/UMD/25122613140997_registration.png',
        },
        {
          name: 'Set up your loyalty program',
          text: 'Customize your colors, messages, and rewards in 5 minutes.',
          image: 'https://cdn.1cdn.app/application/UMD/25122613141056_setup.png',
        },
        {
          name: 'Display QR code in store',
          text: 'Print and display your QR code so customers can sign up.',
          image: 'https://cdn.1cdn.app/application/UMD/2512261314097_qr.png',
        },
      ];

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: locale.startsWith('fr')
      ? 'Comment configurer Super User Manager en 3 étapes'
      : 'How to set up Super User Manager in 3 steps',
    description: locale.startsWith('fr')
      ? 'Guide étape par étape pour configurer votre système de gestion clientèle et programme de fidélité en seulement 10 minutes.'
      : 'Step-by-step guide to set up your customer management system and loyalty program in just 10 minutes.',
    totalTime: 'PT10M',
    estimatedCost: {
      '@type': 'MonetaryAmount',
      currency: 'CHF',
      value: '0',
    },
    supply: [
      {
        '@type': 'HowToSupply',
        name: locale.startsWith('fr') ? 'Ordinateur ou smartphone' : 'Computer or smartphone',
      },
      {
        '@type': 'HowToSupply',
        name: locale.startsWith('fr') ? 'Imprimante (optionnel)' : 'Printer (optional)',
      },
    ],
    tool: [
      {
        '@type': 'HowToTool',
        name: locale.startsWith('fr') ? 'Navigateur web' : 'Web browser',
      },
    ],
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
      image: {
        '@type': 'ImageObject',
        url: step.image,
        width: 800,
        height: 600,
      },
    })),
  };
}

// Service Schema for specific services
export function generateServiceSchema(
  locale: Locale,
  serviceType: 'customer-management' | 'loyalty-program' | 'analytics' | 'gamification' | 'digital-business-cards'
) {
  const services = {
    'customer-management': {
      name: locale.startsWith('fr') ? 'Gestion de Clientèle' : 'Customer Management',
      description: locale.startsWith('fr')
        ? 'Tableau de bord complet pour gérer tous vos clients, leurs informations et leurs interactions avec votre entreprise.'
        : 'Complete dashboard to manage all your customers, their information and interactions with your business.',
      serviceType: 'BusinessService',
      category: locale.startsWith('fr') ? 'Logiciel de gestion' : 'Management Software',
    },
    'loyalty-program': {
      name: locale.startsWith('fr') ? 'Programme de Fidélité Digital' : 'Digital Loyalty Program',
      description: locale.startsWith('fr')
        ? 'Programme de fidélité avec QR code permettant aux clients de collecter des points et recevoir des récompenses automatiquement.'
        : 'QR code loyalty program allowing customers to collect points and receive rewards automatically.',
      serviceType: 'BusinessService',
      category: locale.startsWith('fr') ? 'Marketing digital' : 'Digital Marketing',
    },
    analytics: {
      name: locale.startsWith('fr') ? 'Analyses et Rapports' : 'Analytics and Reports',
      description: locale.startsWith('fr')
        ? 'Analyses détaillées de vos clients, leurs comportements et la performance de votre programme de fidélité.'
        : 'Detailed analytics of your customers, their behaviors and your loyalty program performance.',
      serviceType: 'BusinessService',
      category: locale.startsWith('fr') ? 'Analyse de données' : 'Data Analytics',
    },
    gamification: {
      name: locale.startsWith('fr') ? 'Système de Gamification' : 'Gamification System',
      description: locale.startsWith('fr')
        ? "Ajoutez des jeux interactifs à votre programme de fidélité et boostez l'engagement client. Construisez votre base de données clients 4x plus vite avec des roues de la fortune et des cartes à gratter."
        : 'Add interactive games to your loyalty program and watch customer engagement skyrocket. Build your customer database 4x faster with spin-to-win wheels and scratch cards.',
      serviceType: 'BusinessService',
      category: locale.startsWith('fr') ? 'Engagement client' : 'Customer Engagement',
    },
    'digital-business-cards': {
      name: locale.startsWith('fr') ? 'Cartes de Visite Digitales' : 'Digital Business Cards',
      description: locale.startsWith('fr')
        ? 'Créez et partagez des cartes de visite digitales professionnelles avec QR code pour le networking et les événements.'
        : 'Create and share professional digital business cards with QR code for networking and events.',
      serviceType: 'BusinessService',
      category: locale.startsWith('fr') ? 'Outils professionnels' : 'Professional Tools',
    },
  };

  const service = services[serviceType];

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: 'Super User Manager',
      url: 'https://super-user-manager.com',
    },
    serviceType: service.serviceType,
    category: service.category,
    areaServed: GLOBAL_AREA_SERVED,
    availableChannel: {
      '@type': 'ServiceChannel',
      serviceUrl: 'https://super-user-manager.com',
      servicePhone: '+41 78 314 55 98',
    },
    hoursAvailable: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: getCurrencyForLocale(locale),
      description: locale.startsWith('fr') ? 'Gratuit pour 25 clients' : 'Free for 25 customers',
      availability: 'https://schema.org/InStock',
    },
  };
}
